// src/components/admin/settings/RegionsSettings.jsx
import React, { useState, useEffect } from 'react';
import { AlertCircle, MapPin, Plus, Edit, Trash2 } from 'lucide-react';
import { useQuery, useMutation } from '@apollo/client';
import { gql } from '@apollo/client';

// Import DataTableSettings and ModalComponent
import DataTableSettings from '../../common/DataTableSettings';
import ModalComponent from '../../common/ModalComponent';

// GraphQL Queries
const GET_REGIONS = gql`
  query GetRegions {
    regions {
      id
      nomRegion
      createdAt
      updatedAt
      villeVillages {
        id
        nomVilleVillage
      }
      users {
        id
      }
      pharmacies {
        id
      }
    }
  }
`;

// GraphQL Mutations
const CREATE_REGION = gql`
  mutation CreateRegion($createRegionInput: CreateRegionInput!) {
    createRegion(createRegionInput: $createRegionInput) {
      id
      nomRegion
      createdAt
      updatedAt
    }
  }
`;

const UPDATE_REGION = gql`
  mutation UpdateRegion($updateRegionInput: UpdateRegionInput!) {
    updateRegion(updateRegionInput: $updateRegionInput) {
      id
      nomRegion
      createdAt
      updatedAt
    }
  }
`;

const DELETE_REGION = gql`
  mutation DeleteRegion($id: ID!) {
    removeRegion(id: $id)
  }
`;

const RegionsSettings = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentRegion, setCurrentRegion] = useState(null);
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);
  const [formData, setFormData] = useState({
    nomRegion: ''
  });
  const [formErrors, setFormErrors] = useState({});

  // GraphQL hooks
  const { data, loading, error, refetch } = useQuery(GET_REGIONS, {
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all'
  });

  const [createRegion, { loading: createLoading }] = useMutation(CREATE_REGION, {
    refetchQueries: [{ query: GET_REGIONS }],
    onCompleted: () => {
      setIsModalOpen(false);
      setFormData({ nomRegion: '' });
      setFormErrors({});
    },
    onError: (error) => {
      console.error('Create region error:', error);
      if (error.graphQLErrors?.[0]?.message?.includes('unique')) {
        setFormErrors({ nomRegion: 'Une région avec ce nom existe déjà' });
      } else {
        setFormErrors({ nomRegion: 'Erreur lors de la création de la région' });
      }
    }
  });

  const [updateRegion, { loading: updateLoading }] = useMutation(UPDATE_REGION, {
    refetchQueries: [{ query: GET_REGIONS }],
    onCompleted: () => {
      setIsModalOpen(false);
      setCurrentRegion(null);
      setFormData({ nomRegion: '' });
      setFormErrors({});
    },
    onError: (error) => {
      console.error('Update region error:', error);
      if (error.graphQLErrors?.[0]?.message?.includes('unique')) {
        setFormErrors({ nomRegion: 'Une région avec ce nom existe déjà' });
      } else {
        setFormErrors({ nomRegion: 'Erreur lors de la mise à jour de la région' });
      }
    }
  });

  const [deleteRegion, { loading: deleteLoading }] = useMutation(DELETE_REGION, {
    refetchQueries: [{ query: GET_REGIONS }],
    onCompleted: () => {
      setIsConfirmDeleteOpen(false);
      setCurrentRegion(null);
    },
    onError: (error) => {
      console.error('Delete region error:', error);
      alert('Erreur lors de la suppression de la région');
    }
  });

  // Process regions data for display
  const regions = React.useMemo(() => {
    if (!data?.regions) return [];
    
    return data.regions.map(region => ({
      id: region.id,
      nomRegion: region.nomRegion,
      villesCount: region.villeVillages?.length || 0,
      pharmaciesCount: region.pharmacies?.length || 0,
      usersCount: region.users?.length || 0,
      createdAt: region.createdAt,
      updatedAt: region.updatedAt
    }));
  }, [data]);

  const columns = [
    {
      Header: 'Nom de la région',
      accessor: 'nomRegion',
      sortable: true,
      searchable: true,
      Cell: ({ value }) => (
        <div className="flex items-center">
          <MapPin size={16} className="mr-2 text-gray-400" />
          <span className="font-medium">{value}</span>
        </div>
      )
    },
    {
      Header: 'Villes/Villages',
      accessor: 'villesCount',
      sortable: true,
      searchable: false,
      Cell: ({ value }) => (
        <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
          {value}
        </span>
      )
    },
    {
      Header: 'Pharmacies',
      accessor: 'pharmaciesCount',
      sortable: true,
      searchable: false,
      Cell: ({ value }) => (
        <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">
          {value}
        </span>
      )
    },
    {
      Header: 'Utilisateurs',
      accessor: 'usersCount',
      sortable: true,
      searchable: false,
      Cell: ({ value }) => (
        <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded-full text-xs font-medium">
          {value}
        </span>
      )
    }
  ];

  const handleCreateNew = () => {
    setCurrentRegion(null);
    setFormData({
      nomRegion: ''
    });
    setFormErrors({});
    setIsModalOpen(true);
  };

  const handleEdit = (region) => {
    setCurrentRegion(region);
    setFormData({
      nomRegion: region.nomRegion
    });
    setFormErrors({});
    setIsModalOpen(true);
  };

  const handleDelete = (region) => {
    setCurrentRegion(region);
    setIsConfirmDeleteOpen(true);
  };

  const confirmDelete = async () => {
    if (!currentRegion) return;
    
    try {
      await deleteRegion({
        variables: { id: currentRegion.id }
      });
    } catch (error) {
      console.error('Delete error:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error when field is filled
    if (value && formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.nomRegion?.trim()) {
      errors.nomRegion = 'Le nom de la région est requis';
    } else {
      // Check for duplicate region name (case insensitive)
      const normalizedName = formData.nomRegion.trim().toLowerCase();
      const duplicate = regions.find(r => 
        r.nomRegion.toLowerCase() === normalizedName && 
        (!currentRegion || r.id !== currentRegion.id)
      );
      
      if (duplicate) {
        errors.nomRegion = 'Une région avec ce nom existe déjà';
      }
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    
    const regionData = {
      nomRegion: formData.nomRegion.trim()
    };
    
    try {
      if (currentRegion) {
        // Update existing region
        await updateRegion({
          variables: {
            updateRegionInput: {
              id: currentRegion.id,
              ...regionData
            }
          }
        });
      } else {
        // Create new region
        await createRegion({
          variables: {
            createRegionInput: regionData
          }
        });
      }
    } catch (error) {
      console.error('Submit error:', error);
    }
  };

  // Handle loading and error states
  if (error && !data) {
    return (
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
        <div className="flex items-center">
          <AlertCircle className="mr-2" size={20} />
          <span>Erreur lors du chargement des régions: {error.message}</span>
        </div>
        <button 
          onClick={() => refetch()} 
          className="mt-2 px-3 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700"
        >
          Réessayer
        </button>
      </div>
    );
  }

  return (
    <div>
      <DataTableSettings
        title="Gestion des Régions"
        subtitle="Gérez les régions administratives du pays"
        data={regions}
        columns={columns}
        loading={loading}
        onCreate={handleCreateNew}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />
      
      {/* Create/Edit Modal */}
      <ModalComponent
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setCurrentRegion(null);
          setFormData({ nomRegion: '' });
          setFormErrors({});
        }}
        title={currentRegion ? 'Modifier la région' : 'Créer une nouvelle région'}
        onSubmit={handleSubmit}
        isSubmitting={createLoading || updateLoading}
        submitLabel={currentRegion ? 'Mettre à jour' : 'Créer'}
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nom de la région *
            </label>
            <input
              type="text"
              name="nomRegion"
              value={formData.nomRegion}
              onChange={handleInputChange}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${
                formErrors.nomRegion ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="Grand Casablanca"
              disabled={createLoading || updateLoading}
            />
            {formErrors.nomRegion && (
              <p className="mt-1 text-sm text-red-600">{formErrors.nomRegion}</p>
            )}
          </div>
        </div>
      </ModalComponent>
      
      {/* Confirm Delete Modal */}
      <ModalComponent
        isOpen={isConfirmDeleteOpen}
        onClose={() => {
          setIsConfirmDeleteOpen(false);
          setCurrentRegion(null);
        }}
        title="Confirmer la suppression"
        onSubmit={confirmDelete}
        isSubmitting={deleteLoading}
        submitLabel="Supprimer"
        submitVariant="danger"
      >
        <div className="flex items-center space-x-4 text-red-600 mb-4">
          <AlertCircle size={24} />
          <h3 className="text-lg font-medium">Attention</h3>
        </div>
        <p className="text-gray-500">
          Êtes-vous sûr de vouloir supprimer la région <strong>{currentRegion?.nomRegion}</strong> ?
        </p>
        <p className="text-gray-500 mt-2">
          Cette action est irréversible. Les villes, pharmacies et utilisateurs associés à cette région seront automatiquement détachés.
        </p>
        {currentRegion && (currentRegion.villesCount > 0 || currentRegion.pharmaciesCount > 0 || currentRegion.usersCount > 0) && (
          <div className="mt-4 p-4 bg-yellow-50 border-l-4 border-yellow-400 text-yellow-700">
            <p className="font-medium">Cette région contient :</p>
            <ul className="list-disc ml-5 mt-1">
              {currentRegion.villesCount > 0 && (
                <li>{currentRegion.villesCount} villes/villages</li>
              )}
              {currentRegion.pharmaciesCount > 0 && (
                <li>{currentRegion.pharmaciesCount} pharmacies</li>
              )}
              {currentRegion.usersCount > 0 && (
                <li>{currentRegion.usersCount} utilisateurs</li>
              )}
            </ul>
          </div>
        )}
      </ModalComponent>
    </div>
  );
};

export default RegionsSettings;